/**
 * A demonstrative legend for a Manifest Review board (the board kit's Living
 * Legend shape, docs/design/boards.md).
 *
 * Each entry shows the SAMPLE a row draws (the same chip component the cell
 * uses), the plain word, one line saying what it means, and how many rows of
 * this view carry it. Given `onSelect`, every entry is a filter: pressing it
 * shows only those rows; pressing it again, or "Show all", restores the view.
 * Nothing is hover-only, and colour is never the only carrier: every sample
 * carries its word.
 *
 * `exclusive`: the entries partition the rows (each row is in exactly one), so
 * a share of the whole is drawn; otherwise a row can carry several and only
 * counts are said.
 */
import React, { useId, useState } from 'react';
import { readMemory, writeMemory, memorySlug } from './memory';

export interface MfLegendItem {
  key: string;
  label: string;
  /** The row's own chip, exactly as the cell draws it. */
  sample: React.ReactNode;
  meaning: string;
  count: number;
  /** A colour for the share bar (exclusive legends only). */
  color?: string;
}

const fmt = (n: number) => n.toLocaleString('en-US');

export default function MfLegend({ title, items, whole, units = ['row', 'rows'], exclusive = false, value = null, onSelect, scope, wholeWords = 'in this view' }: {
  title: string;
  items: MfLegendItem[];
  /** The number of rows in the view the counts are of. */
  whole: number;
  units?: [string, string];
  exclusive?: boolean;
  value?: string | null;
  onSelect?: (key: string | null) => void;
  scope?: string;
  /** Where the counts are from ("in this view", "on this page"). */
  wholeWords?: string;
}) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const store = `legend.mf-${memorySlug(title)}`;
  const [folded, setFolded] = useState<boolean>(() => readMemory<boolean>(store, false) === true);
  if (!items.length) return null;
  const unit = (n: number) => (n === 1 ? units[0] : units[1]);
  const pressed = items.find((i) => i.key === value) || null;
  const fold = () => { const next = !folded; setFolded(next); writeMemory(store, next); };
  const titleId = `${uid}-title`;
  const listId = `${uid}-list`;
  return (
    <div className="board-legend mf-legend" role="group" aria-labelledby={titleId} data-folded={folded ? 'true' : 'false'} data-interactive={onSelect ? 'true' : 'false'}>
      <div className="board-legend__head">
        <p id={titleId} className="board-legend__title">{title}</p>
        <p className="board-legend__whole">{fmt(whole)} {unit(whole)} {wholeWords}</p>
        {onSelect && (
          <p className="board-legend__status" aria-live="polite">
            {pressed ? `Showing only ${pressed.label}: ${fmt(pressed.count)} ${unit(pressed.count)}.` : `Showing all ${fmt(whole)} ${unit(whole)}.`}
          </p>
        )}
        <span className="board-legend__actions">
          {onSelect && (
            <button type="button" className="board-legend__all" aria-pressed={!pressed} onClick={() => onSelect(null)}>
              {pressed ? 'Show all' : 'Showing all'}
            </button>
          )}
          <button type="button" className="board-legend__fold" aria-expanded={!folded} aria-controls={listId} onClick={fold}>
            {folded ? 'Show what each means' : 'Hide explanations'}
          </button>
        </span>
      </div>
      {scope && !folded && <p className="board-legend__scope">{scope}</p>}
      <ul className="board-legend__list" id={listId}>
        {items.map((it, i) => {
          const on = value === it.key;
          const pct = exclusive && whole ? Math.round((it.count / whole) * 100) : null;
          // A key may hold spaces or symbols ("TWL - VETTING"): the id is the slug plus the entry's place, so it is always a valid, unique id.
          const descId = `${uid}-k${i}-${memorySlug(it.key)}-desc`;
          const body = (
            <>
              <span className="board-legend__key"><span className="board-legend__sample">{it.sample}</span></span>
              <span className="board-legend__nums">
                <span className="board-legend__count">{fmt(it.count)} {unit(it.count)}</span>
                {pct !== null && <span className="board-legend__pct">{pct}%<span className="board-sr"> of the rows {wholeWords}</span></span>}
              </span>
              {pct !== null && (
                <span className="board-legend__share" aria-hidden="true">
                  <span className="board-legend__sharebar" style={{ width: `${Math.min(100, pct)}%`, background: it.color || 'var(--tide-interact-strong)' }} />
                </span>
              )}
            </>
          );
          return (
            <li key={it.key} className="board-legend__item" data-key={it.key} data-pressed={on ? 'true' : 'false'}>
              {onSelect ? (
                <button type="button" className="board-legend__entry" aria-pressed={on} aria-describedby={descId} onClick={() => onSelect(on ? null : it.key)}>
                  {body}
                </button>
              ) : <div className="board-legend__entry">{body}</div>}
              <p id={descId} className="board-legend__desc" hidden={folded}>{it.meaning}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
